import React, { useState, useEffect } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Shield, Plus, Users, Key } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import RolesList from '@/components/rbac/RolesList';
import PermissionMatrix from '@/components/rbac/PermissionMatrix';
import RoleEditor from '@/components/rbac/RoleEditor';
import { usePermissions } from '@/contexts/PermissionsContext';

const RBACManagement = () => {
  const { hasPermission } = usePermissions();
  const [activeTab, setActiveTab] = useState('roles');
  const [roles, setRoles] = useState({ system_roles: [], custom_roles: [], total: 0 });
  const [permissions, setPermissions] = useState({ modules: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [showRoleEditor, setShowRoleEditor] = useState(false);
  const [editingRole, setEditingRole] = useState(null);

  // Charger les rôles
  const fetchRoles = async () => {
    try {
      const response = await api.get('/rbac/roles');
      setRoles(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des rôles');
      console.error(error);
    }
  };

  // Charger les permissions
  const fetchPermissions = async () => {
    try {
      const response = await api.get('/rbac/permissions');
      setPermissions(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des permissions');
      console.error(error);
    }
  };

  // Charger les données au montage
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchRoles(), fetchPermissions()]);
      setLoading(false);
    };
    loadData();
  }, []);

  // Créer un nouveau rôle
  const handleCreateRole = () => {
    setEditingRole(null);
    setShowRoleEditor(true);
  };

  // Éditer un rôle
  const handleEditRole = (role) => {
    setEditingRole(role);
    setShowRoleEditor(true);
  };

  // Supprimer un rôle
  const handleDeleteRole = async (roleId, roleName) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer le rôle "${roleName}" ?`)) {
      return;
    }

    try {
      await api.delete(`/rbac/roles/${roleId}`);
      toast.success('Rôle supprimé avec succès');
      await fetchRoles();
    } catch (error) {
      const message = error.response?.data?.detail || 'Erreur lors de la suppression du rôle';
      toast.error(message);
    }
  };

  // Sauvegarder un rôle (création ou modification)
  const handleSaveRole = async (roleData) => {
    try {
      if (editingRole) {
        // Modification
        await api.put(`/rbac/roles/${editingRole.id}`, roleData);
        toast.success('Rôle modifié avec succès');
      } else {
        // Création
        await api.post('/rbac/roles', roleData);
        toast.success('Rôle créé avec succès');
      }
      
      await fetchRoles();
      setShowRoleEditor(false);
      setEditingRole(null);
    } catch (error) {
      const message = error.response?.data?.detail || 'Erreur lors de la sauvegarde du rôle';
      toast.error(message);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement de la gestion RBAC...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <Shield className="h-8 w-8 text-emerald-600" />
          <h1 className="text-3xl font-bold text-gray-900">Gestion des Rôles & Permissions</h1>
        </div>
        <p className="text-gray-600">
          Configurez les rôles et les permissions pour contrôler l'accès aux fonctionnalités de l'application.
        </p>
      </div>

      {/* Stats rapides */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-gray-600">Total Permissions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <Key className="h-5 w-5 text-emerald-600" />
              <span className="text-2xl font-bold">{permissions.total}</span>
            </div>
            <p className="text-xs text-gray-500 mt-1">{permissions.modules?.length} modules</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-gray-600">Rôles Système</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-blue-600" />
              <span className="text-2xl font-bold">{roles.system_roles?.length}</span>
            </div>
            <p className="text-xs text-gray-500 mt-1">Rôles prédéfinis</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-gray-600">Rôles Personnalisés</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <Users className="h-5 w-5 text-purple-600" />
              <span className="text-2xl font-bold">{roles.custom_roles?.length}</span>
            </div>
            <p className="text-xs text-gray-500 mt-1">Rôles créés par vous</p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="roles" className="flex items-center gap-2">
            <Shield className="h-4 w-4" />
            Gestion des Rôles
          </TabsTrigger>
          <TabsTrigger value="permissions" className="flex items-center gap-2">
            <Key className="h-4 w-4" />
            Matrice des Permissions
          </TabsTrigger>
        </TabsList>

        {/* Tab 1: Gestion des Rôles */}
        <TabsContent value="roles" className="space-y-4">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h2 className="text-xl font-semibold">Liste des Rôles</h2>
              <p className="text-sm text-gray-600">
                Gérez les rôles système et créez des rôles personnalisés
              </p>
            </div>
            {hasPermission('rbac', 'create_roles') && (
              <Button 
                onClick={handleCreateRole}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                <Plus className="h-4 w-4 mr-2" />
                Créer un Rôle
              </Button>
            )}
          </div>

          <RolesList
            systemRoles={roles.system_roles}
            customRoles={roles.custom_roles}
            onEdit={handleEditRole}
            onDelete={handleDeleteRole}
            permissions={permissions}
          />
        </TabsContent>

        {/* Tab 2: Matrice des Permissions */}
        <TabsContent value="permissions" className="space-y-4">
          <div className="mb-4">
            <h2 className="text-xl font-semibold">Matrice des Permissions</h2>
            <p className="text-sm text-gray-600">
              Vue d'ensemble de toutes les permissions disponibles par module
            </p>
          </div>

          <PermissionMatrix permissions={permissions} />
        </TabsContent>
      </Tabs>

      {/* Modal d'édition de rôle */}
      {showRoleEditor && (
        <RoleEditor
          role={editingRole}
          permissions={permissions}
          onSave={handleSaveRole}
          onCancel={() => {
            setShowRoleEditor(false);
            setEditingRole(null);
          }}
        />
      )}
    </div>
  );
};

export default RBACManagement;
